import { autoUpdate } from '@floating-ui/react-dom';
import { useEffect, useState } from 'react';
import { useOnboardingAnchors } from './anchorContext';
import type { AnchorId } from './steps';

export type AnchorRect = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>;

/**
 * The live box of a registered anchor, or null while it has none. Kept current
 * through scroll, resize and layout shifts, so anything drawn from it follows
 * the control rather than the position it happened to hold on arrival.
 */
export const useAnchorRect = (anchorId: AnchorId) => {
  const anchors = useOnboardingAnchors();
  const [rect, setRect] = useState<AnchorRect | null>(null);

  const anchor = anchors?.getAnchor(anchorId) ?? null;
  const anchorVersion = anchors?.version;

  useEffect(() => {
    if (!anchor) {
      setRect(null);
      return;
    }

    const measure = () => {
      const { top, left, width, height } = anchor.getBoundingClientRect();
      // A control that has not been laid out yet, or a rail animating to or from
      // nothing, has no box worth drawing from: a ring would be an 8px stub in
      // the corner and a scrim would dim the page it is stepping aside from.
      // Cleared rather than left alone, or a control collapsing to nothing would
      // hold its last box and keep the ring painting where it used to be.
      if (width === 0 || height === 0) {
        setRect(null);
        return;
      }
      setRect({ top, left, width, height });
    };

    // Measure up front rather than waiting on autoUpdate's first observer
    // callback: those are delivered with the rendering steps, which a background
    // tab parks, so a step change there would leave the ring on the old control.
    measure();

    // Both arguments are the anchor: autoUpdate only needs elements to observe
    // for scroll, resize and layout shifts, and there is no floating element to
    // position here.
    return autoUpdate(anchor, anchor, measure);
  }, [anchor, anchorVersion]);

  return rect;
};
